import { BALANCE } from '../balance.ts';
import type { Rng } from '../rng.ts';
import type { PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  failResult,
  okResult,
  pickHolder,
  type ActContext,
  type GenContext,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type ViewContext,
} from './types.ts';
import { alterableClues, clueByKind, holderClues, playersWithRole } from './util.ts';

/**
 * Electrical panel (GDD §8.3). The hacker sees the wires and the serial number; the rule
 * manual is on another role's screen. For every wire the *first* matching rule says what to
 * do (cut / connect / leave alone), so the solution is unique by construction.
 */

export const WIRE_COLORS = ['red', 'blue', 'yellow', 'black', 'white', 'green'] as const;
export type WireColor = (typeof WIRE_COLORS)[number];
export type WireAction = 'cut' | 'connect' | 'leave';

export type Condition =
  | { k: 'color'; color: WireColor }
  | { k: 'colorSerialOdd'; color: WireColor }
  | { k: 'colorSerialEven'; color: WireColor }
  | { k: 'position'; where: 'first' | 'last' }
  | { k: 'colorCountAtLeast'; color: WireColor; n: number }
  | { k: 'serialHasLetter'; letter: string; color: WireColor };

export interface Rule {
  cond: Condition;
  act: WireAction;
}

export interface PanelParams {
  wires: WireColor[];
  serial: string;
  rules: Rule[];
  expected: WireAction[];
}

export interface PanelState {
  /** Wires correctly handled so far. */
  done: boolean[];
  mistakes: number;
}

const WIRES_BY_LEVEL = [3, 3, 4, 5, 6, 7] as const;
const RULES_BY_LEVEL = [3, 3, 4, 5, 6, 8] as const;
const PALETTE_BY_LEVEL = [3, 3, 4, 5, 6, 6] as const;
const LETTERS = 'ABCDEFGHJKLMNPRSTUVWXYZ';

const params = (p: PuzzleInstance) => p.params as PanelParams;
const state = (p: PuzzleInstance) => p.state as PanelState;

function lastDigitOdd(serial: string): boolean {
  const digits = serial.replace(/\D/g, '');
  const last = digits.charCodeAt(digits.length - 1) - 48;
  return last % 2 === 1;
}

function matches(cond: Condition, i: number, wires: readonly WireColor[], serial: string): boolean {
  const color = wires[i] as WireColor;
  switch (cond.k) {
    case 'color':
      return color === cond.color;
    case 'colorSerialOdd':
      return color === cond.color && lastDigitOdd(serial);
    case 'colorSerialEven':
      return color === cond.color && !lastDigitOdd(serial);
    case 'position':
      return cond.where === 'first' ? i === 0 : i === wires.length - 1;
    case 'colorCountAtLeast':
      return color === cond.color && wires.filter((w) => w === cond.color).length >= cond.n;
    case 'serialHasLetter':
      return color === cond.color && serial.includes(cond.letter);
  }
}

/** What the manual prescribes for each wire. */
export function expectedActions(
  wires: readonly WireColor[],
  serial: string,
  rules: readonly Rule[],
): WireAction[] {
  return wires.map((_, i) => {
    const rule = rules.find((r) => matches(r.cond, i, wires, serial));
    return rule ? rule.act : 'leave';
  });
}

function randomRule(rng: Rng, palette: readonly WireColor[], serial: string): Rule {
  const color = rng.pick(palette);
  const act: WireAction = rng.pick(['cut', 'cut', 'connect']);
  switch (rng.int(0, 5)) {
    case 0:
      return { cond: { k: 'color', color }, act };
    case 1:
      return { cond: { k: 'colorSerialOdd', color }, act };
    case 2:
      return { cond: { k: 'colorSerialEven', color }, act };
    case 3:
      return { cond: { k: 'position', where: rng.pick(['first', 'last'] as const) }, act };
    case 4:
      return { cond: { k: 'colorCountAtLeast', color, n: 2 }, act };
    default: {
      const letter = serial.charAt(rng.int(0, 1));
      return { cond: { k: 'serialHasLetter', letter, color }, act };
    }
  }
}

function generate(ctx: GenContext) {
  const { rng, level } = ctx;
  const nWires = WIRES_BY_LEVEL[level] ?? 5;
  const nRules = RULES_BY_LEVEL[level] ?? 5;
  const palette = rng.shuffle(WIRE_COLORS).slice(0, PALETTE_BY_LEVEL[level] ?? 4);

  for (let attempt = 0; attempt < 200; attempt++) {
    const wires = Array.from({ length: nWires }, () => rng.pick(palette));
    const serial =
      LETTERS.charAt(rng.int(0, LETTERS.length - 1)) +
      LETTERS.charAt(rng.int(0, LETTERS.length - 1)) +
      String(rng.int(100, 999));
    const rules = Array.from({ length: nRules }, () => randomRule(rng, palette, serial));
    const expected = expectedActions(wires, serial, rules);
    const work = expected.filter((a) => a !== 'leave').length;
    if (work < 1 || work > nWires - 1) continue;
    const p: PanelParams = { wires, serial, rules, expected };
    const s: PanelState = { done: wires.map(() => false), mistakes: 0 };
    return {
      params: p,
      state: s,
      clues: [
        {
          id: 'manual',
          kind: 'panelManual',
          holder: pickHolder(ctx.roles, ['lookout', 'mastermind']),
          value: rules,
        },
      ],
      solvers: ['hacker'] as Role[],
    };
  }
  throw new Error('panel generator failed');
}

function remaining(p: PuzzleInstance): number {
  const par = params(p);
  const st = state(p);
  return par.expected.filter((a, i) => a !== 'leave' && !st.done[i]).length;
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  if (act.kind !== 'panel.act') return failResult('invalid', 'not a panel action');
  const par = params(p);
  const st = state(p);
  if (ctx.actorRole !== 'hacker')
    return failResult('notAllowed', 'only the hacker works the panel');
  if (!Number.isInteger(act.wire) || act.wire < 0 || act.wire >= par.wires.length) {
    return failResult('invalid', 'no such wire');
  }
  if (st.done[act.wire]) return failResult('illegalMove', 'that wire is already handled');
  const audience = playersWithRole(ctx.players, 'hacker');
  if (par.expected[act.wire] === act.act) {
    st.done[act.wire] = true;
    return okResult({
      solved: remaining(p) === 0,
      events: [{ type: 'panel.ok', audience, data: { wire: act.wire, act: act.act } }],
    });
  }
  st.mistakes += 1;
  return okResult({
    errorTenths: BALANCE.penalty.panel,
    undo: { kind: 'none' },
    events: [{ type: 'panel.spark', audience, data: { wire: act.wire } }],
  });
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const base: Record<string, unknown> = {
    type: 'panel',
    solved: p.solved,
    wireCount: par.wires.length,
    clues: holderClues(p, ctx.role),
  };
  if (ctx.role === 'hacker') {
    base['wires'] = par.wires.map((color, i) => ({ color, done: st.done[i] ?? false }));
    base['serial'] = par.serial;
  }
  if (ctx.revealed) base['secret'] = { expected: par.expected };
  return base;
}

function legalActs(p: PuzzleInstance, role: Role): PuzzleAct[] {
  if (role !== 'hacker' || p.solved) return [];
  const st = state(p);
  const out: PuzzleAct[] = [];
  for (let wire = 0; wire < params(p).wires.length; wire++) {
    if (st.done[wire]) continue;
    out.push({ kind: 'panel.act', wire, act: 'cut' }, { kind: 'panel.act', wire, act: 'connect' });
  }
  return out;
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  if (alterableClues(p, exclude).length === 0) return null;
  const clue = clueByKind(p, 'panelManual');
  const rules = (clue.value as Rule[]).map((r) => ({ cond: { ...r.cond }, act: r.act }));
  const i = rng.int(0, rules.length - 1);
  const rule = rules[i] as Rule;
  if ('color' in rule.cond) {
    const others = WIRE_COLORS.filter((c) => c !== (rule.cond as { color: WireColor }).color);
    (rule.cond as { color: WireColor }).color = rng.pick(others);
  } else {
    rule.act = rule.act === 'cut' ? 'connect' : 'cut';
  }
  return { clueId: clue.id, value: rules };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as PanelParams;
  const recomputed = expectedActions(par.wires, par.serial, par.rules);
  const same = recomputed.every((a, i) => a === par.expected[i]);
  const work = recomputed.filter((a) => a !== 'leave').length;
  return { solvable: same && work >= 1, difficulty: par.wires.length + par.rules.length };
}

function autoSolve(p: PuzzleInstance): void {
  const par = params(p);
  const st = state(p);
  par.expected.forEach((a, i) => {
    if (a !== 'leave') st.done[i] = true;
  });
}

export const panelModule: PuzzleModule = {
  type: 'panel',
  generate,
  apply,
  view,
  legalActs,
  tamper,
  autoSolve,
  undo: () => undefined,
  solve,
};
